import React from "react";
import { Easing, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { LOOK, TYPE } from "../look";

const clamp = {
  extrapolateLeft: "clamp",
  extrapolateRight: "clamp",
} as const;

// ---------------------------------------------------------------------------
// Tap — finger press + ripple, positioned by its centre.
// ---------------------------------------------------------------------------

export const Tap: React.FC<{ x: number; y: number; at: number; size?: number }> = ({
  x,
  y,
  at,
  size = 90,
}) => {
  const frame = useCurrentFrame();
  const t = frame - at;
  if (t < -6 || t > 20) return null;
  return (
    <div style={{ position: "absolute", left: x, top: y, pointerEvents: "none" }}>
      {/* Press dot */}
      <div
        style={{
          position: "absolute",
          width: size,
          height: size,
          marginLeft: -size / 2,
          marginTop: -size / 2,
          borderRadius: size,
          background: "rgba(255,255,255,0.55)",
          border: "3px solid rgba(0, 67, 239, 0.55)",
          scale: interpolate(t, [-6, 0, 3, 12], [1.4, 0.8, 0.9, 1], clamp),
          opacity: interpolate(t, [-6, -2, 10, 18], [0, 1, 1, 0], clamp),
        }}
      />
      {/* Ripple */}
      <div
        style={{
          position: "absolute",
          width: size,
          height: size,
          marginLeft: -size / 2,
          marginTop: -size / 2,
          borderRadius: size,
          border: `4px solid ${LOOK.white}`,
          scale: interpolate(t, [0, 16], [0.8, 2.4], {
            ...clamp,
            easing: Easing.bezier(0.16, 1, 0.3, 1),
          }),
          opacity: interpolate(t, [0, 16], [0.9, 0], clamp),
        }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Struck — text with a hand-drawn-ish strike drawn across it at `at`.
// ---------------------------------------------------------------------------

export const Struck: React.FC<{
  children: React.ReactNode;
  at: number;
  color?: string;
  thickness?: number;
  /** Fade the struck text towards this opacity once crossed. */
  dimTo?: number;
}> = ({ children, at, color = LOOK.red, thickness = 12, dimTo = 0.45 }) => {
  const frame = useCurrentFrame();
  const draw = interpolate(frame, [at, at + 8], [0, 1], {
    ...clamp,
    easing: Easing.bezier(0.65, 0, 0.35, 1),
  });
  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <span style={{ opacity: interpolate(draw, [0, 1], [1, dimTo]) }}>{children}</span>
      <span
        style={{
          position: "absolute",
          left: "-4%",
          top: "57%",
          height: thickness,
          width: `${draw * 108}%`,
          marginTop: -thickness / 2,
          background: color,
          borderRadius: thickness,
          rotate: "-4deg",
          transformOrigin: "left center",
        }}
      />
    </span>
  );
};

// ---------------------------------------------------------------------------
// StepLabel — "1  Choisis une marque…" header used by the three steps.
// ---------------------------------------------------------------------------

export const StepLabel: React.FC<{
  n: number;
  text: string;
  sub?: string;
  start?: number;
  dark?: boolean;
  /** Wide layout: number above a bigger, left-aligned label. */
  stacked?: boolean;
}> = ({ n, text, sub, start = 0, dark = false, stacked = false }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const pop = spring({
    frame: frame - start,
    fps,
    config: { damping: 12, stiffness: 220, mass: 0.6 },
  });
  const slide = spring({
    frame: frame - start - 3,
    fps,
    config: { damping: 20, stiffness: 180, mass: 0.7 },
  });
  const ink = dark ? LOOK.white : LOOK.ink;
  const badge = stacked ? 110 : 88;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: stacked ? "column" : "row",
        alignItems: stacked ? "flex-start" : "center",
        gap: stacked ? 36 : 28,
        justifyContent: "center",
      }}
    >
      <div
        style={{
          flex: "none",
          width: badge,
          height: badge,
          borderRadius: badge,
          background: dark ? LOOK.white : LOOK.blue,
          color: dark ? LOOK.blue : LOOK.white,
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: stacked ? 64 : 52,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          scale: pop,
        }}
      >
        {n}
      </div>
      <div style={{ overflow: "hidden", padding: "4px 0 10px", maxWidth: 820 }}>
        <div
          style={{
            translate: `0 ${(1 - slide) * 110}%`,
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: stacked ? 84 : 56,
            lineHeight: 1.05,
            letterSpacing: stacked ? -3 : -2,
            color: ink,
            textWrap: "balance",
          }}
        >
          {text}
        </div>
        {sub ? (
          <div
            style={{
              translate: `0 ${(1 - slide) * 140}%`,
              marginTop: stacked ? 18 : 6,
              fontFamily: LOOK.body,
              fontWeight: LOOK.medium,
              fontSize: stacked ? TYPE.support : TYPE.small,
              color: dark ? LOOK.blueSoft : LOOK.inkSoft,
            }}
          >
            {sub}
          </div>
        ) : null}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Store badges — same composition as the landing hero badges.
// ---------------------------------------------------------------------------

const AppleIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size * 0.75} height={size} viewBox="0 0 30 40" fill="none">
    <path
      d="M24.8984 20.9922C24.8828 18.125 26.1797 15.9609 28.8047 14.3672C27.3359 12.2656 25.1172 11.1094 22.1875 10.8828C19.4141 10.6641 16.3828 12.5 15.2734 12.5C14.1016 12.5 11.4141 10.9609 9.30469 10.9609C4.94531 11.0313 0.3125 14.4375 0.3125 21.3672C0.3125 23.4141 0.6875 25.5286 1.4375 27.7109C2.4375 30.5781 6.04687 37.6094 9.8125 37.4922C11.7812 37.4453 13.1719 36.0938 15.7344 36.0938C18.2187 36.0938 19.5078 37.4922 21.7031 37.4922C25.5 37.4375 28.7656 31.0469 29.7187 28.1719C24.625 25.7734 24.8984 21.1406 24.8984 20.9922ZM20.4766 8.16406C22.6094 5.63281 22.4141 3.32813 22.3516 2.5C20.4688 2.60937 18.2891 3.78125 17.0469 5.22656C15.6797 6.77344 14.875 8.6875 15.0469 10.8438C17.0859 11 18.9453 9.95312 20.4766 8.16406Z"
      fill="#ffffff"
    />
  </svg>
);

const PlayIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size * 0.9} height={size} viewBox="0 0 27.6202 30.8576" fill="none">
    <path d="M12.9025 14.7447L0.121867 28.3042C0.514699 29.7807 1.86252 30.8576 3.46094 30.8576C4.0976 30.8576 4.69362 30.6883 5.21514 30.3767L5.249 30.3496L19.6212 22.046L12.8889 14.7176L12.9025 14.7447Z" fill="#EA4335" />
    <path d="M25.8254 12.4352H25.8118L19.6078 8.82518L12.6113 15.0495L19.6281 22.0663L25.8051 18.5037C26.882 17.9145 27.6202 16.7766 27.6202 15.4695C27.6202 14.1623 26.9023 13.0244 25.8186 12.4487V12.4352H25.8254Z" fill="#FBBC04" />
    <path d="M0.121914 2.57374C0.047411 2.86498 0 3.14944 0 3.461V27.4237C0 27.7353 0.033865 28.0265 0.121914 28.311L13.336 15.0969L0.121914 2.56696V2.57374Z" fill="#4285F4" />
    <path d="M12.9973 15.4356L19.6077 8.82518L5.24222 0.494432C4.71393 0.182875 4.11114 0 3.45416 0C1.85574 0 0.514692 1.07691 0.115087 2.55341L12.9905 15.4288L12.9973 15.4356Z" fill="#34A853" />
  </svg>
);

export const StoreBadge: React.FC<{
  store: "apple" | "google";
  small: string;
  name: string;
  style?: React.CSSProperties;
}> = ({ store, small, name, style }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 18,
      padding: "18px 34px 18px 28px",
      borderRadius: 22,
      background: "#000",
      border: "2px solid rgba(255,255,255,0.25)",
      color: LOOK.white,
      ...style,
    }}
  >
    {store === "apple" ? <AppleIcon size={50} /> : <PlayIcon size={44} />}
    <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
      <span style={{ fontFamily: LOOK.body, fontSize: 20, fontWeight: LOOK.medium, opacity: 0.9 }}>
        {small}
      </span>
      <span
        style={{
          fontFamily: LOOK.display,
          fontSize: 38,
          fontWeight: LOOK.semi,
          letterSpacing: -0.5,
          marginTop: 4,
        }}
      >
        {name}
      </span>
    </div>
  </div>
);
